import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { useTabDrafts } from '@/stores/tabDrafts'

/** Outcome of `save()`. The close coordinator branches on it. */
export type SaveResult = { ok: true } | { ok: false; message: string }

export type EditorDraftOptions = {
  /** Enable per-tab persistence via the useTabDrafts store. */
  persist?: {
    /** The owning tab's id (opaque key; '' disables persistence). */
    tabId: string
    /** Persist the latest draft snapshot on unmount. Default: false */
    persistOnUnmount?: boolean
  }
  /** Built-in save with try/catch + toast + commit. */
  onSave?: (draft: string) => Promise<unknown>
  /** Adopt a server-normalized draft, including its new revision, after Save. */
  savedDraftFromResult?: (result: unknown) => string
  /** Custom success toast message. Default: 'Saved successfully' */
  successMessage?: string
  /**
   * External dirty flag OR'd with the markdown dirty state.
   * Useful when the editor is part of a larger form (e.g. document metadata fields).
   */
  externalDirty?: boolean
}

export type EditorDraft = {
  /** Current draft value (may differ from baseline while user is editing). */
  draft: string
  /** Key to pass to the editor so it remounts when baseline or cancel resets content. */
  editorKey: number
  /** Whether the draft differs from the baseline (OR'd with externalDirty when provided). */
  dirty: boolean
  /** Guarded onChange — suppresses stale Milkdown destroy flushes during cancel. */
  onChange: (md: string) => void
  /** Reset draft to the current baseline and remount the editor (cancel). */
  reset: () => void
  /**
   * Swap the draft for `next` and remount the editor.
   *
   * Guarded the same way `reset` is: the outgoing editor flushes whatever it
   * holds through `onChange` as it is destroyed, which would otherwise land
   * after the swap and put the abandoned text back.
   */
  replace: (next: string) => void
  /** Mark the current draft as the new optimistic baseline (call after save succeeds). */
  commit: () => void
  /**
   * Calls onSave(draft), commits, and toasts. Resolves with the outcome so a
   * caller (the close coordinator) can tell success from a handled failure.
   * With no onSave: ok when clean, a failure when dirty — an editor cannot
   * flush work it was given no way to send.
   */
  save: () => Promise<SaveResult>
  /** Loading state for save. */
  isSaving: boolean
}

/**
 * Encapsulates the draft / baseline / optimistic-update / editor-key pattern
 * used by every Milkdown editor.
 *
 * Supports optional tab persistence and built-in save handling.
 *
 * @param serverValue The latest value from the server query (e.g. `conversation?.script`).
 *                    Pass `''` when loading or unavailable.
 */
export function useEditorDraft(
  serverValue: string,
  options?: EditorDraftOptions
): EditorDraft {
  const tabId = options?.persist?.tabId
  const persistOnUnmount = options?.persist?.persistOnUnmount ?? false
  const externalDirty = options?.externalDirty ?? false

  // Refs for callback options — avoids recreating callbacks when the caller
  // passes inline arrow functions. Direct assignment during render is safe
  // for refs and keeps them current without extra effects.
  const onSaveRef = useRef(options?.onSave)
  onSaveRef.current = options?.onSave
  const savedDraftFromResultRef = useRef(options?.savedDraftFromResult)
  savedDraftFromResultRef.current = options?.savedDraftFromResult

  const successMessageRef = useRef(options?.successMessage)
  successMessageRef.current = options?.successMessage

  // --- Core draft state ---

  const [optimisticBaseline, setOptimisticBaseline] = useState<string | null>(
    null
  )
  const baseline = optimisticBaseline ?? serverValue

  // Lazy init: restore from stored dirty draft if available. Writes/reads
  // here use `useTabDrafts.getState()` to avoid subscribing the editor to
  // its own keystroke-rate writes (would defeat the keystroke-perf
  // property the old _draftVersion counter provided).
  // A restored dirty draft is NOT pristine, even when it happens to equal the
  // baseline it was stored against (both '' while the query is still loading).
  const hydratedDirtyRef = useRef(false)

  const [draft, setDraft] = useState<string>(() => {
    if (tabId) {
      const stored = useTabDrafts.getState().getDraft(tabId)
      // Verbatim, including '': emptying a document is an edit like any other,
      // and a background/remount cycle must not undo it.
      if (stored?.dirty) {
        hydratedDirtyRef.current = true
        return stored.current
      }
    }
    return serverValue
  })

  const [editorKey, setEditorKey] = useState(0)
  const prevBaselineRef = useRef(baseline)
  const cancellingRef = useRef(false)

  const baselineRef = useRef(baseline)
  baselineRef.current = baseline

  const draftRef = useRef(draft)
  draftRef.current = draft

  const dirty = draft !== baseline
  const combinedDirty = dirty || externalDirty

  const dirtyRef = useRef(combinedDirty)
  dirtyRef.current = combinedDirty

  // --- Baseline sync ---
  // Sync baseline -> draft only when user hasn't edited.
  //
  // hydratedDirtyRef is what keeps a deliberate deletion: open meeting notes,
  // select all, delete, switch tabs. The draft ('') persists, the tab
  // unmounts, and on return the notes query is still loading — so the restored
  // draft equals the empty baseline and looks pristine. When the notes land a
  // moment later this effect would adopt them and silently undo the deletion.
  useEffect(() => {
    const prevBaseline = prevBaselineRef.current
    if (
      !hydratedDirtyRef.current &&
      draftRef.current === prevBaseline &&
      baseline !== prevBaseline
    ) {
      // Guarded like `reset`: this remounts the editor, and the outgoing one
      // flushes whatever it holds through `onChange` as it is destroyed. Left
      // unguarded that flush lands after the swap and puts the previous text
      // back — which, when the previous text was the empty pre-load baseline,
      // silently empties the draft the moment real content arrives.
      cancellingRef.current = true
      setDraft(baseline)
      setEditorKey((k) => k + 1)
    }
    prevBaselineRef.current = baseline
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseline])

  // Clear optimistic baseline once server catches up.
  useEffect(() => {
    if (optimisticBaseline == null) return
    if (serverValue === optimisticBaseline) {
      setOptimisticBaseline(null)
    }
  }, [optimisticBaseline, serverValue])

  // Held until the editor swap has actually committed. A microtask is too
  // short: a swap triggered from an effect commits after microtasks flush, so
  // the outgoing editor's destroy flush would arrive unguarded and put the
  // abandoned text back.
  useEffect(() => {
    cancellingRef.current = false
  }, [editorKey])

  // --- Guarded onChange ---
  // Any of these three ends the restored-draft window: from here the draft is
  // this session's own, and the pristine compare above tells the truth again.
  const onChange = useCallback((md: string) => {
    if (cancellingRef.current) return
    hydratedDirtyRef.current = false
    setDraft(md)
  }, [])

  // --- Reset draft to baseline and remount editor ---
  const replace = useCallback(
    (next: string) => {
      cancellingRef.current = true
      hydratedDirtyRef.current = false
      setEditorKey((k) => k + 1)
      setDraft(next)
      if (tabId) {
        useTabDrafts.getState().clearDraft(tabId)
      }
    },
    [tabId]
  )

  const reset = useCallback(() => {
    replace(baselineRef.current)
  }, [replace])

  // --- Optimistically update baseline to current draft ---
  const commit = useCallback(() => {
    hydratedDirtyRef.current = false
    setOptimisticBaseline(draftRef.current)
    if (tabId) {
      useTabDrafts.getState().clearDraft(tabId)
    }
  }, [tabId])

  // --- Tab persistence: write draft after render ---
  useEffect(() => {
    if (!tabId) return
    const drafts = useTabDrafts.getState()
    if (drafts.isClosing(tabId)) return
    const existing = drafts.getDraft(tabId)
    drafts.setDraft(tabId, {
      baseline: baselineRef.current,
      current: draftRef.current,
      dirty: draftRef.current !== baselineRef.current || externalDirty,
      formFields: existing?.formFields,
    })
    // `baseline` is a dep because a late server value can flip dirty without
    // touching the draft — a restored deletion that the sync effect refuses to
    // adopt. Leaving it out stored that draft as clean and dropped the prompt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, draft, baseline, externalDirty])

  // --- Tab persistence: persist on unmount ---
  useEffect(() => {
    if (!tabId || !persistOnUnmount) return
    return () => {
      const drafts = useTabDrafts.getState()
      if (drafts.isClosing(tabId)) return
      const existing = drafts.getDraft(tabId)
      drafts.setDraft(tabId, {
        baseline: baselineRef.current,
        current: draftRef.current,
        dirty:
          draftRef.current !== baselineRef.current ||
          Boolean(existing?.dirty && existing?.formFields),
        formFields: existing?.formFields,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, persistOnUnmount])

  // --- Save handler ---
  const [isSaving, setIsSaving] = useState(false)

  const save = useCallback(async (): Promise<SaveResult> => {
    const fn = onSaveRef.current
    if (!fn) {
      // Unsaved work with nowhere to send it: the close dialog would otherwise
      // offer a Save that silently discards the draft.
      return dirtyRef.current
        ? { ok: false, message: 'Nothing to save' }
        : { ok: true }
    }
    setIsSaving(true)
    try {
      const result = await fn(draftRef.current)
      const savedDraft = savedDraftFromResultRef.current?.(result)
      if (savedDraft !== undefined) {
        draftRef.current = savedDraft
        setDraft(savedDraft)
      }
      hydratedDirtyRef.current = false
      setOptimisticBaseline(draftRef.current)
      if (tabId) {
        useTabDrafts.getState().clearDraft(tabId)
      }
      toast.success(successMessageRef.current ?? 'Saved successfully')
      return { ok: true }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save'
      toast.error(message)
      return { ok: false, message }
    } finally {
      setIsSaving(false)
    }
  }, [tabId])

  return {
    draft,
    editorKey,
    dirty: combinedDirty,
    onChange,
    reset,
    replace,
    commit,
    save,
    isSaving,
  }
}
